import { InfoCard } from '@/entities/widget';
import { TagIcon } from 'lucide-react';
import { DetailList } from '../DetailList/DetailList';
import { DetailSection } from '../DetailSection/DetailSection';
import { DetailSectionItem } from '../DetailSectionItem/DetailSectionItem';
import { DetailStack } from '../DetailStack/DetailStack';
import { SectionTitle } from '../SectionTitle/SectionTitle';
import type { BrandContextSectionProps } from './BrandContextSection.types';
import { useTranslation } from 'react-i18next';

export function BrandContextSection(props: BrandContextSectionProps) {
  const { t } = useTranslation('contentTest');

  return (
    <div data-testid="social-brief-brand-context-section">
      <SectionTitle
        title={t('socialBriefWidget.brandContextSection.brandContext')}
        icon={<TagIcon className="size-5" />}
        tooltip={t('socialBriefWidget.brandContextSection.outlinesHowTheBrandShows')}
      />
      <DetailSection>
        <BrandMentions {...props} />
        <CompetitiveMentions {...props} />
        <KeyMessages {...props} />
        <ToneOfVoice {...props} />
      </DetailSection>
    </div>
  );
}

function BrandMentions({ item }: BrandContextSectionProps) {
  const { t } = useTranslation('contentTest');

  return (
    <DetailStack>
      <span className="text-body-lg font-medium text-foreground">
        {t('socialBriefWidget.brandContextSection.brandMentions', {
          brandName: item.brandName,
        })}
      </span>

      {item.brandContext.brandMentions.map((mention) => (
        <DetailSectionItem
          key={mention.label}
          title={mention.label}
          description={mention.description}
          sources={{ title: mention.source, url: mention.source }}
        />
      ))}
    </DetailStack>
  );
}

function CompetitiveMentions({ item }: BrandContextSectionProps) {
  const { t } = useTranslation('contentTest');

  return (
    <DetailStack>
      <span className="text-body-lg font-medium text-foreground">
        {t('socialBriefWidget.brandContextSection.competitiveMentions')}
      </span>

      {item.brandContext.competitiveMentions.map((mention) => (
        <DetailSectionItem
          key={mention.label}
          title={mention.label}
          description={mention.description}
          sources={{ title: mention.source, url: mention.source }}
        />
      ))}
    </DetailStack>
  );
}

function KeyMessages({ item }: BrandContextSectionProps) {
  const { t } = useTranslation('contentTest');

  return (
    <DetailStack>
      <span className="text-body-lg font-medium text-foreground">
        {t('socialBriefWidget.brandContextSection.keyMessagesClientPovAlignment')}
      </span>

      {item.brandContext.keyMessages.map((message) => (
        <DetailSectionItem
          key={message.label}
          title={message.label}
          description={message.description}
          sources={{ title: message.source, url: message.source }}
        />
      ))}
    </DetailStack>
  );
}

function ToneOfVoice({ item }: BrandContextSectionProps) {
  const { t } = useTranslation('contentTest');

  return (
    <DetailStack>
      <span className="text-body-lg font-medium text-foreground">
        {t('socialBriefWidget.brandContextSection.toneOfVoice')}
      </span>

      <InfoCard style="w-full">
        <DetailStack>
          <span className="text-body-lg font-medium text-muted-foreground">
            {t('socialBriefWidget.brandContextSection.style')}
          </span>

          <span className="text-body-lg text-foreground">
            {item.brandContext.toneOfVoice.styles.join(', ')}
          </span>
        </DetailStack>
      </InfoCard>

      <InfoCard style="w-full">
        <DetailStack>
          <span className="text-body-lg font-medium text-muted-foreground">
            {t('socialBriefWidget.brandContextSection.creativeGuidance')}
          </span>

          <DetailList>
            {item.brandContext.toneOfVoice.creativeGuidance.map((tone) => (
              <li key={tone} className="text-body-lg text-foreground">
                {tone}
              </li>
            ))}
          </DetailList>
        </DetailStack>
      </InfoCard>
    </DetailStack>
  );
}
